import React, { useState, useRef } from 'react';
import {
	Typography,
	InputGroup,
	Button,
	Div,
	Tag,
} from '@icecreamsocial/components';
import {
	CloseIcon,
	AddNoteIcon,
} from '../../../../../presentational/SupportedIcons';
import { useStore } from '../../../../../hooks';
import {
	ChonkyInput,
	OfferHint,
	PersonalMessageToggle,
} from '../../../../../presentational';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

export const Email = ({ ...rest }) => {
	const MORE_SHARE_OPTIONS = '/share';

	/**
	 * set up some hooks
	 */
	const intl = useIntl();
	const store = useStore();
	const history = useHistory();
	const inputRef = useRef();
	const [emails, setEmails] = useState([]);
	const [error, setError] = useState('');
	const [message, setMessage] = useState('');

	/**
	 * non-component localization strings
	 */
	const DUPLICATE_ERROR = intl.formatMessage({
		id: 'share.email.duplicateError',
		description:
			'Error that is displayed when the user has entered a duplicate email',
		defaultMessage: 'Whoops! You already added that email.',
	});

	/**
	 * component functions
	 */
	const appendEmail = (value) => {
		setEmails([...emails, value]);
	};

	const removeEmail = (index) => {
		const updated = [...emails];
		updated.splice(index, 1);
		setEmails([...updated]);
	};

	const hasDuplicates = (toAdd) => {
		return emails.indexOf(toAdd) > -1;
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const value = inputRef.current.value;
		setError('');

		if (hasDuplicates(value)) {
			setError(DUPLICATE_ERROR);
			e.target.reset();
			return;
		}

		appendEmail(inputRef.current.value);
		e.target.reset();
	};

	const sendEmails = async () => {
		const { success } = await store.sendEmail(message, emails);
		if (success) {
			history.push('/thank-you');
		}
	};

	return (
		<>
			<Typography.H1 textAlign='center'>
				<FormattedMessage
					id='share.email.heading'
					defaultMessage={'Invite friends by email'}
					description={
						'Heading that tells user to invite friends by emailing them'
					}
				/>
			</Typography.H1>
			<Typography.P>
				<FormattedMessage
					id='share.email.subheading'
					defaultMessage={"Enter your friends' email addresses below"}
					description={
						"Subheading that instructs user to enter their friend's emails in the input fields below"
					}
				/>
			</Typography.P>

			<form onSubmit={handleSubmit}>
				<InputGroup elWidth='100%'>
					<InputGroup.Label>
						<FormattedMessage
							id='share.email.inputLabel'
							defaultMessage={"Friend's Email Address"}
							description={
								"Email input label for the user to enter their friend's email address"
							}
						/>
					</InputGroup.Label>
					<InputGroup.Fields alignItems='center'>
						<ChonkyInput
							ref={inputRef}
							name={`invite-email`}
							type='email'
							placeholder='friend@email.com'
							autoComplete='off'
							paddingLeft='md'
							required
						/>
						<InputGroup.Actions>
							<InputGroup.Action type='submit'>
								<FormattedMessage
									id='share.email.inputSubmit'
									defaultMessage={'Enter'}
									description={
										'Button for submitting the email the user has entered'
									}
								/>
							</InputGroup.Action>
						</InputGroup.Actions>
					</InputGroup.Fields>
					{error && (
						<Typography.Span textColor='bad' display='block' marginTop='sm'>
							{error}
						</Typography.Span>
					)}
				</InputGroup>
			</form>

			<Div
				display='flex'
				flexWrap='wrap'
				width='100%'
				alignItems='center'
				style={{ gap: '8px' }}
				marginTop='md'
			>
				{emails.map((email, i) => (
					<Button
						as={Tag}
						key={i}
						onClick={() => removeEmail(i)}
						display='flex !important'
						alignItems='center'
						paddingLeft='sm'
						paddingRight='sm'
						style={{ gap: '8px' }}
					>
						<Typography.Span>{email}</Typography.Span>
						<CloseIcon size={20} textColor='ambientDark' />
					</Button>
				))}
			</Div>

			<PersonalMessageToggle onUpdate={setMessage} />

			<Button
				textAlign='center'
				elWidth='100%'
				textColor='bodyInverse'
				backgroundColor='accent'
				marginTop='lg'
				marginBottom='xlg'
				disabled={!emails.length}
				onClick={sendEmails}
			>
				<FormattedMessage
					id='share.email.invitesSubmit'
					defaultMessage={'Send Invites'}
					description={'Button for sending invites to all the entered emails'}
				/>
			</Button>
			<OfferHint conversionGoal={5} />
			<Div textAlign='center' marginBottom='xlg'>
				<Typography.A
					as={Link}
					to={MORE_SHARE_OPTIONS}
					textColor='body'
					visitedTextColor='body'
				>
					<FormattedMessage
						id='share.email.moreOptions'
						defaultMessage={'More Share Options'}
						description={
							'Link to take a user back to view more options for sharing'
						}
					/>
				</Typography.A>
			</Div>
		</>
	);
};
